import React from 'react';
import { Skeleton } from '../ui/skeleton';

/**
 * Skeleton building blocks shared across pages. The layout mirrors the real
 * components so the transition into loaded content doesn't shift the page.
 */

export function TaskListSkeleton({ rows = 7 }) {
  return (
    <div className="space-y-2 md:bg-card md:border md:border-border md:rounded-xl md:divide-y md:divide-border/60 md:overflow-hidden">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3 md:px-4 py-3 max-md:rounded-xl max-md:bg-card max-md:border max-md:border-border"
        >
          <Skeleton className="h-5 w-5 rounded-full flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-4" style={{ width: `${50 + ((i * 37) % 40)}%` }} />
            <div className="flex items-center gap-2">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function ProjectGridSkeleton({ rows = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4" style={{ width: `${50 + ((i * 53) % 40)}%` }} />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatsRowSkeleton({ cards = 3 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PageHeaderSkeleton({ withSubtitle = true }) {
  return (
    <div className="space-y-2">
      <Skeleton className="h-7 md:h-8 w-48" />
      {withSubtitle && <Skeleton className="h-4 w-72 max-w-full" />}
    </div>
  );
}

/**
 * Generic fallback used by React.Suspense when a lazy-loaded route chunk is
 * downloading. Mirrors the AppLayout's content padding so the bar doesn't jump
 * once the page mounts.
 */
export function RouteChunkFallback() {
  return (
    <div className="space-y-4">
      <PageHeaderSkeleton />
      <TaskListSkeleton rows={6} />
    </div>
  );
}
